import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "../styles/admin.css"; 
import { getApiBase, withBase } from "../config/api"; 
import {
  Newspaper,
  LayoutList,
  Package,
  Image,
  Quote,
  GalleryVertical,
} from "lucide-react";

// --- Helper component for stat cards ---
const StatCard = ({ title, value, icon, link, color }) => {
  const Icon = icon;
  return (
    // FIXED: Added 'position-relative' here so the link doesn't cover the whole page
    <div className="admin-card h-100 position-relative">
      <div className="card-body p-3">
        <div className="d-flex align-items-center">
          <div
            className={`flex-shrink-0 me-3 p-3 rounded`}
            style={{ backgroundColor: `${color}20` }} 
          >
            <Icon size={24} style={{ color: color }} />
          </div>
          <div className="flex-grow-1">
            <div className="admin-stat-label text-muted">{title}</div>
            <div className="admin-stat-number h2 mb-0">{value}</div>
          </div>
        </div>
        {link && (
          <Link to={link} className="stretched-link">
            <span className="visually-hidden">View {title}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

// --- Reusable Paginated Table Component ---
const PaginatedTable = ({ title, data, columns, itemsPerPage = 5, linkToManage, linkToCreate }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination logic
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = data.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="admin-card h-100 position-relative">
      <div className="card-body d-flex flex-column">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{title}</h5>
          <div style={{ zIndex: 2, position: 'relative' }}> {/* Ensure buttons are clickable */}
            {linkToCreate && (
              <Link to={linkToCreate} className="btn btn-sm btn-admin-primary me-2">
                + Add New
              </Link>
            )}
            {linkToManage && (
              <Link to={linkToManage} className="btn btn-sm btn-outline-secondary">
                Manage All
              </Link>
            )}
          </div>
        </div>

        {/* Table */}
        {data.length > 0 ? (
          <>
            <div className="table-responsive" style={{ minHeight: '320px' }}>
              <table className="table table-sm table-striped table-hover align-middle">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.header} style={col.style || {}}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item) => (
                    <tr key={item.id}>
                      {columns.map((col) => (
                        <td key={col.key} className="text-muted small" style={{maxWidth: '250px'}}>
                          {col.render ? col.render(item) : <span className="text-truncate">{item[col.key]}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <nav className="mt-auto pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="btn-group" style={{ zIndex: 2, position: 'relative' }}>
                    <button 
                      className="btn btn-sm btn-outline-secondary" 
                      onClick={() => goToPage(currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      &larr; Previous
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary" 
                      onClick={() => goToPage(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      Next &rarr;
                    </button>
                  </div>
                </div>
              </nav>
            )}
          </>
        ) : (
          <p className="text-muted text-center py-5 mb-0">No {title.toLowerCase()} found.</p>
        )}
      </div>
    </div>
  );
};


// --- Main Admin Dashboard Component ---
const AdminDashboard = () => {
  const [stats, setStats] = useState({
    newsCount: 0,
    blogCount: 0,
    collectionCount: 0,
    productImageCount: 0,
    galleryCategoryCount: 0,
    quoteCount: 0,
  });
  const [allNews, setAllNews] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Column Definitions for Tables ---
  const newsAndBlogColumns = [
    {
      header: 'Image',
      key: 'image',
      style: { width: '80px' },
      render: (item) => (
        item.image ? (
          <img
            src={withBase(item.image)}
            alt={item.title}
            className="rounded"
            style={{ width: "60px", height: "40px", objectFit: "cover" }}
          />
        ) : <div style={{width: '60px', height: '40px'}} className="bg-light rounded d-flex align-items-center justify-content-center text-muted small">No Img</div>
      )
    },
    {
      header: 'Title',
      key: 'title',
      render: (item) => <strong className="text-dark text-truncate d-block" style={{maxWidth: '200px'}}>{item.title}</strong>
    },
    {
      header: 'Date',
      key: 'date',
      style: { width: '120px' },
      render: (item) => new Date(item.date).toLocaleDateString()
    }
  ];

  const quoteColumns = [
    {
      header: 'Name',
      key: 'name',
      render: (item) => <strong className="text-dark">{item.name}</strong>
    },
    {
      header: 'Business',
      key: 'business',
    },
    {
      header: 'Phone',
      key: 'phone',
    },
    {
      header: 'Date',
      key: 'date',
      style: { width: '120px' },
      render: (item) => new Date(item.date).toLocaleDateString()
    }
  ];


  const fetchDashboardData = useCallback(async () => {
    const BASE = getApiBase();
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${BASE}/api/news`),
        axios.get(`${BASE}/api/blogs`),
        axios.get(`${BASE}/api/products-all`),
        axios.get(`${BASE}/api/gallery-all`),
        axios.get(`${BASE}/api/quotes`),
      ]);

      const [newsRes, blogsRes, productsRes, galleryRes, quotesRes] = results;

      let newsCount = 0, blogCount = 0, collectionCount = 0, 
          productImageCount = 0, galleryCategoryCount = 0, quoteCount = 0;

      if (newsRes.status === 'fulfilled') {
        newsCount = newsRes.value.data.length;
        setAllNews(newsRes.value.data);
      }

      if (blogsRes.status === 'fulfilled') {
        blogCount = blogsRes.value.data.length;
        setAllBlogs(blogsRes.value.data);
      }

      if (productsRes.status === 'fulfilled') {
        const productData = productsRes.value.data;
        collectionCount = productData.flatMap(cat => cat.subcollections).length;
        productImageCount = productData
          .flatMap(cat => cat.subcollections)
          .reduce((acc, col) => acc + col.images.length, 0);
      }

      if (galleryRes.status === 'fulfilled') {
        galleryCategoryCount = galleryRes.value.data.categories.length;
      }

      if (quotesRes.status === 'fulfilled') {
        quoteCount = quotesRes.value.data.length;
        setAllQuotes(quotesRes.value.data);
      }

      setStats({
        newsCount, blogCount, collectionCount,
        productImageCount, galleryCategoryCount, quoteCount
      });

    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="container py-5 vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-4">
        {/* Dashboard Header */}
        <div className="row mb-4 align-items-center">
          <div className="col">
            <h1 className="display-4 fw-bold admin-dashboard-title">
              Admin Dashboard
            </h1>
            <p className="lead text-muted">Welcome to the HS India admin panel.</p>
          </div>
        </div>

        {/* --- Statistic Cards --- */}
        <div className="row g-3 mb-4">
          <div className="col-lg-4 col-md-6">
            <StatCard title="Total Blog Posts" value={stats.blogCount} icon={LayoutList} link="/bloglist" color="#0d6efd" />
          </div>
          <div className="col-lg-4 col-md-6">
            <StatCard title="Total News Articles" value={stats.newsCount} icon={Newspaper} link="/newslist" color="#198754" />
          </div>
          <div className="col-lg-4 col-md-6">
            {/* FIXED: Link is now correctly pointing to quotes */}
            <StatCard title="New Quote Requests" value={stats.quoteCount} icon={Quote} link="/admin/quotes" color="#6f42c1" />
          </div>
          <div className="col-lg-4 col-md-6">
            <StatCard title="Product Collections" value={stats.collectionCount} icon={Package} link="/admin/products" color="#dc3545" />
          </div>
          <div className="col-lg-4 col-md-6">
            <StatCard title="Gallery Categories" value={stats.galleryCategoryCount} icon={GalleryVertical} link="/admin/gallery" color="#ffc107" />
          </div>
          <div className="col-lg-4 col-md-6">
            <StatCard title="Total Product Images" value={stats.productImageCount} icon={Image} link="/admin/products" color="#0dcaf0" />
          </div>
        </div>

        {/* --- Paginated Tables --- */}
        <div className="row g-4">
          <div className="col-12">
            <PaginatedTable
              title="Recent Quote Requests"
              data={allQuotes}
              columns={quoteColumns}
              itemsPerPage={5}
              linkToManage="/admin/quotes"
            />
          </div>
          <div className="col-lg-6">
            <PaginatedTable
              title="Recent News"
              data={allNews}
              columns={newsAndBlogColumns}
              itemsPerPage={5}
              linkToManage="/admin/newslist"
              linkToCreate="/admin/editor"
            />
          </div>
          <div className="col-lg-6">
            <PaginatedTable
              title="Recent Blogs"
              data={allBlogs}
              columns={newsAndBlogColumns}
              itemsPerPage={5}
              linkToManage="/admin/bloglist"
              linkToCreate="/admin/blog-editor"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;